import { Box, ValidationMessage } from '@modusign/design-system';
import cx from 'classnames';
import debounce from 'lodash/debounce';
import { ChangeEvent, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useMount } from 'react-use';

import EnhancedToolTip from '@app/components/EnhancedToolTip';
import { containsEmoji } from '@app/libs/validation';

import './SignerFormRoleField.scss';

function roleValidator(value: string, otherSignerRoles: Array<string>) {
  const trimmedValue = value.trim();
  const isOverlap = otherSignerRoles.includes(trimmedValue);

  if (!trimmedValue) {
    return '역할은 필수 입력사항입니다.';
  }
  if (trimmedValue.length > 10) {
    return '1자 이상 10자 이하로 입력해 주세요.';
  }
  if (isOverlap) {
    return '중복된 역할입니다. 다시 입력해 주세요.';
  }
  if (containsEmoji(value)) {
    return '이모지는 사용할 수 없습니다.';
  }

  return true;
}

interface SignerFormRoleFieldProps {
  signerId: string;
}

function SignerFormRoleField({ signerId }: SignerFormRoleFieldProps) {
  const [isTouched, setIsTouched] = useState(false);
  const [showRoleToolTip, setShowRoleToolTip] = useState(false);

  const { control, errors, getValues, trigger } = useFormContext();

  const {
    field: { ref, value, onChange },
  } = useController({
    control,
    defaultValue: '',
    name: `${signerId}.role`,
    rules: {
      validate: (value) => {
        const signers = getValues();
        const trimmedOtherSignerRoles = Object.keys(signers)
          .filter((key) => key !== signerId)
          .map((key) => signers[key].role?.trim());
        return roleValidator(value, trimmedOtherSignerRoles);
      },
    },
  });

  const error = errors[signerId]?.role?.message;

  useMount(() => {
    if (ref.current) {
      ref.current.focus();
    }
  });

  function handleSetIsTouched() {
    if (!isTouched) {
      setIsTouched(true);
    }
  }

  function handleBlur() {
    handleSetIsTouched();
    setShowRoleToolTip(false);
  }

  const debounceTrigger = debounce(() => {
    // 현재 입력 중인 인풋 외 다른 인풋도 같이 검증하여 role의 중복체크를 합니다.
    trigger();
  }, 600);

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    handleSetIsTouched();
    onChange(e);
    debounceTrigger();
  }

  function handleFocus() {
    setShowRoleToolTip(true);
  }

  return (
    <Box
      className={cx({
        InputSet: true,
        'InputSet--theme-editor': true,
        'InputSet--invalid': isTouched && error,
      })}
      mb={3}
    >
      <input
        aria-label={`${signerId}.role`}
        className={cx({
          'InputSet__text-input': true,
          'InputSet__text-input--with-clear-button': !!value,
        })}
        name="role"
        placeholder="역할"
        ref={ref}
        type="text"
        value={value}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
      />
      <EnhancedToolTip
        arrowDirection="left"
        className={cx({
          'SignerFormRoleField__role-tooltip': true,
          'SignerFormRoleField__role-tooltip--active': !value && showRoleToolTip,
        })}
      >
        <p>
          계약서 내에서 해당 서명자의 역할을 입력해 주세요.
          <br />
          {"예시) '임차인', '근로자', '갑', '을'"}
        </p>
      </EnhancedToolTip>
      {error && (
        <ValidationMessage fontSize="sm" fontWeight="regular">
          {error}
        </ValidationMessage>
      )}
    </Box>
  );
}

export default SignerFormRoleField;
